// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  RegattaBoatRentStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
  numberValue,
} from '@vroskus/library-helpers/dist/browser';
import {
  regattaBoatRentStatusLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaBoatRentFields from '../../../fields/RegattaBoatRentFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaBoatRent'>>;
  loading: boolean;
};

class RegattaBoatRentsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaBoatRent') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaBoatRent({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaBoatRent'> = {
      Content: null,
      Email: '',
      Image: null,
      Name: '',
      Phone: null,
      Price: 0,
      RegattaId,
      RegistrationNo: null,
      Status: RegattaBoatRentStatus.available,
    };

    return (
      <SectionHeader
        bottomBorder
        description={t('A list of boats that that are available for rent in this regatta')}
        iconClass={'icon-star'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaBoatRentFields item={newItem} />
            )}
            id={'create-regatta-boat-rent-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaBoatRent'>>({
                  ...newItem,
                  ...values,
                  Price: numberValue(values.Price),
                });

                await Services.Api.upsertRegattaBoatRent(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta boat rent')}
          >
            <Button
              color={'info'}
              id={'create-regatta-boat-rent-button'}
              size={'sm'}
              title={t('Add new regatta boat rent')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta boat rents')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaBoatRent'>) {
    const {
      t,
    } = this.props;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaBoatRentFields item={dataItem} />
          )}
          id={`update-regatta-boat-rent-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaBoatRent'>>({
                ...dataItem,
                ...values,
                Price: numberValue(values.Price),
              });

              await Services.Api.upsertRegattaBoatRent(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta boat rent')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-boat-rent-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-boat-rent-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaBoatRent(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta boat rent')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-boat-rent-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th>{t('Status')}</th>
            <th>{t('Reg. no.')}</th>
            <th>{t('Name')}</th>
            <th>{`${t('Price')} (${'Eur'})`}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaBoatRent'>) => (
            <tr key={dataItem.id}>
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>{t(regattaBoatRentStatusLabel[dataItem.Status])}</td>
              <td>{dataItem.RegistrationNo}</td>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Price}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaBoatRentsSectionView;
